import { parseFormattedNumber } from '@/features/order/lib/number-format-util'
import {
  type AnchorPrice,
  computeEstimatedPrice,
  type OffsetUnit,
  type PriceAnchor,
} from '@/features/order/order-form/lib/resolve-anchor-price'
import { safeParseFloat } from '@/lib/utils'

/** How far a limit price may cross the touch before the form warns (1%). */
export const PRICE_DEVIATION_THRESHOLD = 0.01

export interface PriceDeviation {
  /** The side of the book the order crosses into: best ask for a buy, best bid for a sell. */
  reference: 'best_ask' | 'best_bid'
  referencePrice: number
  /** How far past the reference the limit sits, as a fraction (0.05 = 5%). Always positive. */
  deviation: number
}

/**
 * Fat-finger check for a limit price. A buy priced more than `threshold` above the best ask, or
 * a sell more than `threshold` below the best bid, crosses the book far enough to sweep levels
 * the user likely didn't mean to take. A resting order (buy under the ask, sell over the bid)
 * never warns. Null when the price or the needed side of the book is missing.
 */
export function checkLimitPriceDeviation(
  side: 'buy' | 'sell',
  limitPrice: number,
  ticker: AnchorPrice | undefined,
  threshold: number = PRICE_DEVIATION_THRESHOLD
): PriceDeviation | null {
  if (!ticker || !Number.isFinite(limitPrice) || limitPrice <= 0) return null

  if (side === 'buy') {
    const ask = safeParseFloat(ticker.bestAskPrice ?? '')
    if (!(ask > 0)) return null
    const deviation = (limitPrice - ask) / ask
    return deviation > threshold ? { reference: 'best_ask', referencePrice: ask, deviation } : null
  }

  const bid = safeParseFloat(ticker.bestBidPrice ?? '')
  if (!(bid > 0)) return null
  const deviation = (bid - limitPrice) / bid
  return deviation > threshold ? { reference: 'best_bid', referencePrice: bid, deviation } : null
}

export interface LimitPriceDeviationRow {
  /** `symbol:side` — a ticker split across accounts warns once, not once per account. */
  key: string
  symbol: string
  side: 'buy' | 'sell'
  deviation: PriceDeviation
}

interface DeviationItem {
  symbol?: string
  side?: 'buy' | 'sell'
}

/**
 * Runs the fat-finger check over every row of a limit ticket. The limit each row would actually
 * send is resolved the same way the batch preview does: an absolute `formPrice` as typed, or a
 * relative anchor + offset against that row's own live book and tick size.
 */
export function findLimitPriceDeviations({
  items,
  formPrice,
  priceAnchor,
  priceOffsetUnit,
  tickerBySymbol,
  tickSizeBySymbol,
  threshold = PRICE_DEVIATION_THRESHOLD,
}: {
  items: readonly (DeviationItem | undefined)[]
  formPrice: string
  priceAnchor: PriceAnchor
  priceOffsetUnit: OffsetUnit
  tickerBySymbol: Record<string, AnchorPrice | undefined>
  tickSizeBySymbol?: Record<string, number | undefined>
  threshold?: number
}): LimitPriceDeviationRow[] {
  const rows: LimitPriceDeviationRow[] = []
  for (const item of items) {
    if (!item?.symbol || !item.side) continue
    const key = `${item.symbol}:${item.side}`
    if (rows.some((row) => row.key === key)) continue
    const ticker = tickerBySymbol[item.symbol]
    const limitPrice =
      priceAnchor === 'price'
        ? parseFormattedNumber(formPrice || '0')
        : computeEstimatedPrice(
            priceAnchor,
            safeParseFloat(formPrice),
            priceOffsetUnit,
            ticker,
            tickSizeBySymbol?.[item.symbol]
          )
    if (limitPrice === null) continue
    const deviation = checkLimitPriceDeviation(item.side, limitPrice, ticker, threshold)
    if (deviation) rows.push({ key, symbol: item.symbol, side: item.side, deviation })
  }
  return rows
}
